import { useEffect, useRef, useState } from "react";
import { api, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ImageOff, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pw = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });
const adminImg = (s) => `${BACKEND}${s.image_url}?admin=${encodeURIComponent(getAdminToken())}&v=${encodeURIComponent(s.updated_at || "")}`;
const toDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function AdminGameCards() {
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const zipInput = useRef(null);
  const replaceInputs = useRef({});

  const load = () => api.get("/game/illustrations").then((r) => setSlots(r.data));
  useEffect(() => { load(); }, []);

  const importedCount = slots.filter((s) => s.has_image).length;

  const onZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await toDataUrl(file);
      const { data } = await api.post("/admin/game/illustrations/import", { zip_base64: dataUrl }, pw());
      await load();
      let msg = `${data.imported_count} illustration(s) importée(s). ${data.present_count}/21 au total.`;
      if (data.missing?.length) msg += ` Manquantes : ${data.missing.join(", ")}.`;
      toast.success(msg);
      if (data.errors?.length) data.errors.forEach((er) => toast.error(er));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    } finally {
      setBusy(false);
      if (zipInput.current) zipInput.current.value = "";
    }
  };

  const onReplace = async (number, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await toDataUrl(file);
      await api.put(`/admin/game/illustrations/${number}`, { image_base64: dataUrl, filename: file.name }, pw());
      await load();
      toast.success(`Carte ${String(number).padStart(2, "0")} remplacée`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remplacement impossible");
    } finally {
      if (replaceInputs.current[number]) replaceInputs.current[number].value = "";
    }
  };

  return (
    <div data-testid="admin-gamecards">
      <div className="rounded-2xl bg-white border border-[#E8DCC4] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-serif text-xl text-[#1F2A24]">Illustrations du Jeu numérique</p>
            <p className="font-sans text-sm text-[#1F2A24]/60">L'Île de l'Abondance — 21 cartes. Association automatique via le manifeste JSON inclus dans le ZIP (ou par numéro dans le nom de fichier).</p>
          </div>
          <span data-testid="illustrations-progress" className="font-serif text-lg text-[#385E3A]">{importedCount} / 21 illustrations importées</span>
        </div>
        <div className="mt-4">
          <input ref={zipInput} type="file" accept=".zip,application/zip" onChange={onZip} className="hidden" data-testid="illustrations-zip-input" />
          <button data-testid="illustrations-import-btn" disabled={busy} onClick={() => zipInput.current?.click()} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm hover:bg-[#2c4a2e] disabled:opacity-50">
            <Upload size={16} /> {busy ? "Import en cours…" : "Importer le pack des 21 illustrations"}
          </button>
          <p className="font-sans text-xs text-[#1F2A24]/45 mt-2">Fichier ZIP contenant les 21 images + le fichier JSON (cards[].image_file → number). À défaut de JSON, un numéro 01–21 dans le nom de fichier est utilisé. Les cartes non associées sont signalées.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5" data-testid="illustrations-grid">
        {slots.map((s) => (
          <div key={s.number} data-testid={`illustration-card-${s.number}`} className="rounded-xl border border-[#E8DCC4] bg-white overflow-hidden">
            <div className="aspect-[3/4] bg-[#F8F5EE] flex items-center justify-center overflow-hidden">
              {s.has_image ? (
                <img src={adminImg(s)} alt={`Carte ${s.number}`} className="w-full h-full object-cover" data-testid={`illustration-thumb-${s.number}`} />
              ) : (
                <div className="text-center text-[#1F2A24]/35" data-testid={`illustration-missing-${s.number}`}>
                  <ImageOff size={28} className="mx-auto" />
                  <p className="font-sans text-xs mt-1">Manquante</p>
                </div>
              )}
            </div>
            <div className="p-2.5 flex items-center justify-between">
              <span className="font-ui text-sm text-[#1F2A24]">Carte {String(s.number).padStart(2, "0")}</span>
              <input ref={(el) => (replaceInputs.current[s.number] = el)} type="file" accept="image/*" className="hidden" data-testid={`illustration-replace-input-${s.number}`} onChange={(e) => onReplace(s.number, e)} />
              <button data-testid={`illustration-replace-btn-${s.number}`} onClick={() => replaceInputs.current[s.number]?.click()} className="inline-flex items-center gap-1 rounded-full border border-[#C9A24B] text-[#1F2A24] px-3 py-1 font-ui text-xs hover:bg-[#C9A24B]/10">
                <RefreshCw size={12} /> Remplacer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
